import {useState, useEffect, forwardRef, useImperativeHandle} from "react";
import {Box, Paper} from "@mui/material";
import {
  useSendMessageMutation,
  useGetResponseQuery,
  useGetChatHistoryQuery,
  IMessage,
  MessageRoleType,
  useUpdateChatHistoryMutation,
} from "./store/api/aiAssistantApiSlice";
import TopBarMainUI from "./components/top-bar/TopBarMainUI";
import EmptyBodyUI from "./components/body/EmptyBodyUI";
import PromptInputUI from "./components/prompt-bar/PromptInputUI";
import ChatBodyUI from "./components/body/ChatBodyUI";
import {PanelState} from "../../hooks/usePanelControl";
import STYLES from "./STYLES";

// Define the ref type for functions exposed to parent
export interface AIAssistantRefHandle {
  handleSendMessage: (messageInput: string) => Promise<void>;
}

interface AIAssistantFeatureProps {
  panelState?: PanelState;
  onResizeCallback: (size: PanelState) => void;
  surface: {
    surfaceType: string;
    surfaceId: string;
    context: string;
  };
  isAutoFocusDisabled?: boolean;
}

const AIAssistantFeature = forwardRef<
  AIAssistantRefHandle,
  AIAssistantFeatureProps
>(
  (
    {
      panelState = PanelState.COLLAPSED,
      onResizeCallback,
      surface,
      isAutoFocusDisabled = false,
    },
    ref,
  ) => {
    const [promptInput, setPromptInput] = useState("");
    const [currentMessageReqId, setCurrentMessageReqId] = useState<
      string | null
    >(null);

    // RTK Query hooks
    const {data: chatSession} = useGetChatHistoryQuery({
      surfaceId: surface.surfaceId,
      surfaceType: surface.surfaceType,
    });
    const [updateChatHistory] = useUpdateChatHistoryMutation();
    function updateChatHistoryWithMessage(message: IMessage) {
      updateChatHistory({
        surfaceId: surface.surfaceId,
        surfaceType: surface.surfaceType,
        message,
      });
    }
    const [sendMessage, {isLoading: isSending}] = useSendMessageMutation();
    const {data: responseData} = useGetResponseQuery(
      currentMessageReqId ?? "",
      {
        skip: !currentMessageReqId,
        pollingInterval: 2000,
      },
    );
    const isPolling = currentMessageReqId !== null;

    // Expose handleSendMessage to parent component via ref
    useImperativeHandle(ref, () => ({
      handleSendMessage,
    }));

    // When polling response is complete, reset currentRequestId
    useEffect(() => {
      if (
        responseData?.completed &&
        responseData?.messageId === currentMessageReqId &&
        currentMessageReqId
      ) {
        setCurrentMessageReqId(null);
        /**
         * TODO: Display the response in the chat body with animation
         */

        /**
         * Update chatSession with the new message (role: assistant)
         */
        updateChatHistoryWithMessage({
          id: responseData.messageId,
          role: MessageRoleType.ASSISTANT,
          value: responseData.response ?? "",
          timestamp: new Date(),
          followUpPrompts: responseData.followUpPrompts,
        });
      }
    }, [responseData, currentMessageReqId]);

    const handleSendMessage = async (
      messageInput: string,
      messageId?: string,
    ) => {
      if (messageInput.trim() === "") return;

      try {
        const result = await sendMessage({
          surfaceId: surface.surfaceId,
          surfaceType: surface.surfaceType,
          context: surface.context,
          message: messageInput.trim(),
          messageId,
        }).unwrap();

        /**
         * Set the current message request id, required for polling
         */
        setCurrentMessageReqId(result.messageId);

        /**
         * Set the prompt input to empty
         */
        setPromptInput("");

        /**
         * Update chatSession with the new message (role: user)
         * Use the mutation to update the chat history
         */
        updateChatHistoryWithMessage({
          id: result.messageId,
          role: MessageRoleType.USER,
          value: messageInput,
          timestamp: new Date(),
        });
      } catch (error) {
        console.error("Failed to send message:", error);
      }
    };

    const handleSuggestionClick = (suggestion: string) => {
      setPromptInput(suggestion);
    };

    return (
      <Paper elevation={0} sx={STYLES.assistantPaper}>
        {/* <TopBarMainUI
          panelState={panelState}
          onResizeCallback={onResizeCallback}
        /> */}

        {/* CHAT BODY */}
        <Box sx={STYLES.assistantChatBody}>
          {!chatSession?.messages || chatSession.messages.length === 0 ? (
            <EmptyBodyUI onSuggestionItemClick={handleSuggestionClick} />
          ) : (
            <ChatBodyUI
              handleSendMessage={handleSendMessage}
              messages={chatSession.messages}
              isPolling={isPolling}
            />
          )}
        </Box>

        <Box flex={1} />

        {/* INPUT AREA */}
        <Box sx={STYLES.assistantPromptInputBox}>
          <PromptInputUI
            promptInput={promptInput}
            setPromptInput={setPromptInput}
            handleSendMessage={handleSendMessage}
            isSending={isSending}
            isPolling={isPolling}
            isAutoFocusDisabled={isAutoFocusDisabled}
          />
        </Box>
      </Paper>
    );
  },
);

AIAssistantFeature.displayName = "AIAssistantFeature";
export default AIAssistantFeature;
